import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { StudyPeriod, StudyPeriodBarnResponse, StudyPeriodCategory, StudyPeriodGrowthInterval, StudyPeriodGrowthReference, StudyPeriodSetup } from '@/types';
import ApiClient from '@/plugins/store/actions/api/ApiClient';

const DEFAULT_OPTIONS = {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
};

export const queryKeys = {
    getStudyPeriodsRoot: ['study-periods'],
    getStudyPeriods: (params: Ref<{ animalGroupId: string; from: string; to: string }>) => [...queryKeys.getStudyPeriodsRoot, params],
    getBarnStudyPeriods: (barnIds: Ref<number[]>, selectedPeriod: Ref<string>) => [...queryKeys.getStudyPeriodsRoot, { barnIds, selectedPeriod }],
    getStudyPeriodsFavorites: (animalGroupId: Ref<string>) => [...queryKeys.getStudyPeriodsRoot, 'favorites', { animalGroupId }],
    getGrowthIntervals: () => ['growth-intervals'],
    getGrowthReferences: (animalGroupId: Ref<string>) => ['growth-references', { animalGroupId }],
    getStudyPeriodCategories: () => ['study-period-categories'],
    getStudyPeriodSetups: () => ['study-period-setups'],
};

export function useBarnStudyPeriods({ barnIds, selectedPeriod }: { barnIds: Ref<number[]>; selectedPeriod: Ref<string> }, options: UseQueryOptions<StudyPeriodBarnResponse> = {}) {
    return useQuery<StudyPeriodBarnResponse>({
        queryKey: queryKeys.getBarnStudyPeriods(barnIds, selectedPeriod),
        queryFn: () =>
            store.dispatch(ActionType.GetStudyPeriods, {
                barnIds: barnIds.value,
            }),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

export function useStudyPeriods(params: Ref<{ animalGroupId: string; from: string; to: string }>, options: UseQueryOptions<StudyPeriod[]> = {}) {
    return useQuery<StudyPeriod[]>({
        queryKey: queryKeys.getStudyPeriods(params),
        queryFn: () => ApiClient.send('get', `monitor/study-periods?animal_group=${params.value.animalGroupId}&from=${params.value.from}&to=${params.value.to}`, {}),
        ...DEFAULT_OPTIONS,
        ...options,
    });
}
export function useStudyPeriodCategories(options: UseQueryOptions<StudyPeriodCategory[]> = {}) {
    return useQuery<StudyPeriodCategory[]>({
        queryKey: queryKeys.getStudyPeriodCategories(),
        queryFn: () => ApiClient.send('get', 'monitor/study-periods/categories', {}),
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

export function useStudyPeriodsFavorites({ animalGroupId }: { animalGroupId: Ref<string> }, options: UseQueryOptions<StudyPeriod[]> = {}) {
    return useQuery<StudyPeriod[]>({
        queryKey: queryKeys.getStudyPeriodsFavorites(animalGroupId),
        queryFn: () => ApiClient.send('get', `monitor/study-periods/favorites?animal_group=${animalGroupId.value}`, {}),
        ...options,
    });
}

export function useStudyPeriodGrowthIntervals(options: UseQueryOptions<StudyPeriodGrowthInterval[]> = {}) {
    return useQuery<StudyPeriodGrowthInterval[]>({
        queryKey: queryKeys.getGrowthIntervals(),
        queryFn: () => ApiClient.send('get', 'monitor/study-periods/growth-intervals', {}),
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

export function useStudyPeriodGrowthReferences({ animalGroupId }: { animalGroupId: Ref<string> }, options: UseQueryOptions<StudyPeriodGrowthReference[]> = {}) {
    return useQuery<StudyPeriodGrowthReference[]>({
        queryKey: queryKeys.getGrowthReferences(animalGroupId),
        queryFn: () => ApiClient.send('get', `monitor/study-periods/growth-reference?animal_group=${animalGroupId.value}`, {}),
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

export function useStudyPeriodSetups(options: UseQueryOptions<StudyPeriodSetup[]> = {}) {
    return useQuery<StudyPeriodSetup[]>({
        queryKey: queryKeys.getStudyPeriodSetups(),
        queryFn: () => ApiClient.send('get', 'monitor/study-periods/setup', {}),
        ...DEFAULT_OPTIONS,
        ...options,
    });
}
